/**
 * Control Precision Section for A4 Summary
 *
 * Visualizes the difference between mechanical and smart thermostats:
 * - Hysteresis comparison graph (temperature swing)
 * - Smart Start vs Standard Start timing
 * - Fuel waste education
 */

import React from 'react';
import type { ThermostatControlType } from '@core/types';

interface ControlPrecisionSectionProps {
  currentThermostatType?: ThermostatControlType;
  recommendedThermostatType?: ThermostatControlType;
  boilerSupportsLoadCompensation?: boolean;
  printMode?: boolean;
}

/**
 * SVG icon for mechanical dial thermostat
 */
const MechanicalThermostatIcon = ({ size = 64 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Dial base */}
    <circle cx="32" cy="32" r="28" fill="#f5f5f5" stroke="#9ca3af" strokeWidth="2" />
    {/* Outer ring markings */}
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = (i * 30 - 90) * (Math.PI / 180);
      const x1 = 32 + 22 * Math.cos(angle);
      const y1 = 32 + 22 * Math.sin(angle);
      const x2 = 32 + 26 * Math.cos(angle);
      const y2 = 32 + 26 * Math.sin(angle);
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#6b7280" strokeWidth="2" />;
    })}
    {/* Temperature dial */}
    <circle cx="32" cy="32" r="16" fill="#dc2626" />
    {/* Dial pointer */}
    <line x1="32" y1="32" x2="32" y2="18" stroke="white" strokeWidth="3" strokeLinecap="round" />
    {/* Center dot */}
    <circle cx="32" cy="32" r="4" fill="white" />
    {/* Temperature text */}
    <text x="32" y="52" textAnchor="middle" fontSize="8" fill="#374151" fontWeight="600">
      21°C
    </text>
  </svg>
);

/**
 * SVG icon for smart digital thermostat
 */
const SmartThermostatIcon = ({ size = 64 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Device body */}
    <rect x="8" y="8" width="48" height="48" rx="8" fill="#1e293b" />
    {/* Screen */}
    <rect x="12" y="12" width="40" height="32" rx="4" fill="#0f172a" />
    {/* Temperature display */}
    <text x="32" y="34" textAnchor="middle" fontSize="16" fill="#22c55e" fontFamily="monospace" fontWeight="700">
      21.0°
    </text>
    {/* Status indicator */}
    <circle cx="48" cy="16" r="3" fill="#22c55e" />
    {/* Wi-Fi icon */}
    <path d="M28 18 Q32 14 36 18" stroke="#6b7280" strokeWidth="1.5" fill="none" />
    <path d="M30 20 Q32 17 34 20" stroke="#6b7280" strokeWidth="1.5" fill="none" />
    <circle cx="32" cy="22" r="1" fill="#6b7280" />
    {/* Bottom buttons */}
    <rect x="16" y="48" width="12" height="4" rx="1" fill="#374151" />
    <rect x="36" y="48" width="12" height="4" rx="1" fill="#374151" />
  </svg>
);

/**
 * Hysteresis comparison graph showing temperature oscillation
 */
const HysteresisGraph = ({ printMode = false }: { printMode?: boolean }) => {
  const width = printMode ? 280 : 320;
  const height = printMode ? 120 : 140;
  const padding = { top: 20, right: 20, bottom: 30, left: 40 };
  const graphWidth = width - padding.left - padding.right;
  const graphHeight = height - padding.top - padding.bottom;

  // Target temperature line
  const targetY = padding.top + graphHeight / 2;

  // Generate mechanical oscillation path (large sine wave)
  const mechanicalPoints: string[] = [];
  for (let x = 0; x <= graphWidth; x += 2) {
    const progress = x / graphWidth;
    const cycles = 3; // Number of oscillations
    const amplitude = graphHeight * 0.4; // ±2°C swing
    const y = targetY - amplitude * Math.sin(progress * cycles * 2 * Math.PI);
    mechanicalPoints.push(`${padding.left + x},${y}`);
  }

  // Generate load compensation path (nearly flat line with tiny ripple)
  const smartPoints: string[] = [];
  for (let x = 0; x <= graphWidth; x += 2) {
    const progress = x / graphWidth;
    const cycles = 8;
    const amplitude = graphHeight * 0.02; // ±0.1°C ripple
    const y = targetY - amplitude * Math.sin(progress * cycles * 2 * Math.PI);
    smartPoints.push(`${padding.left + x},${y}`);
  }

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {/* Background */}
      <rect x={padding.left} y={padding.top} width={graphWidth} height={graphHeight} fill="#f8fafc" />

      {/* Grid lines */}
      {[0.25, 0.5, 0.75].map((frac) => (
        <line
          key={frac}
          x1={padding.left}
          y1={padding.top + graphHeight * frac}
          x2={padding.left + graphWidth}
          y2={padding.top + graphHeight * frac}
          stroke="#e2e8f0"
          strokeDasharray="4,4"
        />
      ))}

      {/* Target temperature line */}
      <line
        x1={padding.left}
        y1={targetY}
        x2={padding.left + graphWidth}
        y2={targetY}
        stroke="#22c55e"
        strokeWidth="2"
        strokeDasharray="6,3"
      />
      <text x={padding.left + 4} y={targetY - 4} fontSize="9" fill="#22c55e" fontWeight="600">
        Target 21°C
      </text>

      {/* Mechanical oscillation */}
      <polyline
        points={mechanicalPoints.join(' ')}
        fill="none"
        stroke="#dc2626"
        strokeWidth="2.5"
      />

      {/* Smart/load compensation */}
      <polyline
        points={smartPoints.join(' ')}
        fill="none"
        stroke="#2563eb"
        strokeWidth="2.5"
      />

      {/* Y-axis labels */}
      <text x={padding.left - 4} y={padding.top + 8} fontSize="8" fill="#6b7280" textAnchor="end">
        +3°C
      </text>
      <text x={padding.left - 4} y={targetY + 3} fontSize="8" fill="#6b7280" textAnchor="end">
        0
      </text>
      <text x={padding.left - 4} y={padding.top + graphHeight - 2} fontSize="8" fill="#6b7280" textAnchor="end">
        -3°C
      </text>

      {/* X-axis label */}
      <text x={padding.left + graphWidth / 2} y={height - 4} fontSize="9" fill="#6b7280" textAnchor="middle">
        Time (hours)
      </text>

      {/* Legend */}
      <g transform={`translate(${padding.left + graphWidth - 100}, ${padding.top + 8})`}>
        <line x1="0" y1="0" x2="20" y2="0" stroke="#dc2626" strokeWidth="2" />
        <text x="24" y="3" fontSize="8" fill="#dc2626">Mechanical</text>

        <line x1="0" y1="12" x2="20" y2="12" stroke="#2563eb" strokeWidth="2" />
        <text x="24" y="15" fontSize="8" fill="#2563eb">Load Comp.</text>
      </g>
    </svg>
  );
};

/**
 * Smart Start timing comparison visualization
 */
const SmartStartComparison = ({ printMode = false }: { printMode?: boolean }) => {
  const width = printMode ? 280 : 320;
  const height = 100;
  const padding = { top: 15, right: 20, bottom: 25, left: 60 };
  const graphWidth = width - padding.left - padding.right;

  // Time markers (hours from 5:00 to 7:30)
  const timeLabels = ['05:00', '05:30', '06:00', '06:30', '07:00', '07:30'];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {/* Standard Start */}
      <g transform={`translate(0, ${padding.top})`}>
        <text x={padding.left - 8} y="10" fontSize="9" fill="#6b7280" textAnchor="end">
          Standard
        </text>
        {/* Cold zone (before 6:00) */}
        <rect x={padding.left} y="0" width={graphWidth * 0.4} height="16" fill="#dbeafe" rx="2" />
        {/* Heating zone */}
        <rect x={padding.left + graphWidth * 0.4} y="0" width={graphWidth * 0.5} height="16" fill="#fecaca" rx="2" />
        {/* Warm zone */}
        <rect x={padding.left + graphWidth * 0.9} y="0" width={graphWidth * 0.1} height="16" fill="#bbf7d0" rx="2" />
        {/* Labels */}
        <text x={padding.left + graphWidth * 0.2} y="12" fontSize="7" fill="#1e40af" textAnchor="middle">
          Cold
        </text>
        <text x={padding.left + graphWidth * 0.65} y="12" fontSize="7" fill="#991b1b" textAnchor="middle">
          Warming up...
        </text>
        <text x={padding.left + graphWidth * 0.4} y="-3" fontSize="7" fill="#6b7280" textAnchor="middle">
          06:00 ON
        </text>
      </g>

      {/* Smart Start */}
      <g transform={`translate(0, ${padding.top + 28})`}>
        <text x={padding.left - 8} y="10" fontSize="9" fill="#6b7280" textAnchor="end">
          Smart
        </text>
        {/* Pre-heat zone */}
        <rect x={padding.left + graphWidth * 0.04} y="0" width={graphWidth * 0.36} height="16" fill="#fef3c7" rx="2" />
        {/* Warm zone (at target by 6:00) */}
        <rect x={padding.left + graphWidth * 0.4} y="0" width={graphWidth * 0.6} height="16" fill="#bbf7d0" rx="2" />
        {/* Labels */}
        <text x={padding.left + graphWidth * 0.22} y="12" fontSize="7" fill="#92400e" textAnchor="middle">
          Pre-heating
        </text>
        <text x={padding.left + graphWidth * 0.7} y="12" fontSize="7" fill="#166534" textAnchor="middle">
          At target 21°C
        </text>
        <text x={padding.left + graphWidth * 0.04} y="-3" fontSize="7" fill="#22c55e" textAnchor="middle">
          05:10 ON
        </text>
      </g>

      {/* Time axis */}
      <g transform={`translate(0, ${height - padding.bottom + 8})`}>
        {timeLabels.map((label, i) => {
          const x = padding.left + (graphWidth * i) / (timeLabels.length - 1);
          return (
            <text key={label} x={x} y="0" fontSize="8" fill="#6b7280" textAnchor="middle">
              {label}
            </text>
          );
        })}
      </g>

      {/* Target time marker */}
      <line
        x1={padding.left + graphWidth * 0.4}
        y1={padding.top - 5}
        x2={padding.left + graphWidth * 0.4}
        y2={height - padding.bottom - 5}
        stroke="#22c55e"
        strokeWidth="1.5"
        strokeDasharray="4,2"
      />
      <text
        x={padding.left + graphWidth * 0.4}
        y={height - padding.bottom + 2}
        fontSize="8"
        fill="#22c55e"
        fontWeight="600"
        textAnchor="middle"
      >
        Wake 06:00
      </text>
    </svg>
  );
};

/**
 * Main Control Precision Section component
 */
export const ControlPrecisionSection: React.FC<ControlPrecisionSectionProps> = ({
  currentThermostatType = 'mechanical',
  recommendedThermostatType = 'smart_load_comp',
  boilerSupportsLoadCompensation = true,
  printMode = false,
}) => {
  const containerStyle: React.CSSProperties = {
    padding: printMode ? '8mm' : 'var(--spacing-lg)',
    background: printMode ? 'white' : 'var(--color-surface)',
    borderRadius: printMode ? '4px' : 'var(--radius-lg)',
    border: '1px solid var(--color-border)',
    pageBreakInside: 'avoid',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: printMode ? '14pt' : 'var(--font-size-xl)',
    fontWeight: 700,
    marginBottom: printMode ? '4mm' : 'var(--spacing-md)',
    color: 'var(--color-text-primary)',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: printMode ? '11pt' : 'var(--font-size-md)',
    fontWeight: 600,
    marginBottom: printMode ? '2mm' : 'var(--spacing-sm)',
    color: 'var(--color-text-secondary)',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: printMode ? '1fr 1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: printMode ? '6mm' : 'var(--spacing-lg)',
    marginBottom: printMode ? '6mm' : 'var(--spacing-lg)',
  };

  return (
    <div style={containerStyle} className="control-precision-section">
      <h3 style={titleStyle}>Control Precision Comparison</h3>

      {/* Thermostat Icons Comparison */}
      <div style={gridStyle}>
        {/* Mechanical */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: printMode ? '4mm' : 'var(--spacing-md)',
            padding: printMode ? '3mm' : 'var(--spacing-md)',
            background: '#fef2f2',
            borderRadius: '8px',
            border: '1px solid #fecaca',
          }}
        >
          <MechanicalThermostatIcon size={printMode ? 48 : 64} />
          <div>
            <h4
              style={{
                fontSize: printMode ? '10pt' : 'var(--font-size-md)',
                fontWeight: 600,
                margin: 0,
                color: '#991b1b',
              }}
            >
              Mechanical Dial
            </h4>
            <p
              style={{
                fontSize: printMode ? '8pt' : 'var(--font-size-sm)',
                margin: '2px 0 0 0',
                color: '#b91c1c',
              }}
            >
              ±1.5-3°C swing
            </p>
            <p
              style={{
                fontSize: printMode ? '8pt' : 'var(--font-size-sm)',
                margin: '2px 0 0 0',
                color: '#7f1d1d',
                fontWeight: 600,
              }}
            >
              10-15% fuel waste
            </p>
          </div>
        </div>

        {/* Smart */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: printMode ? '4mm' : 'var(--spacing-md)',
            padding: printMode ? '3mm' : 'var(--spacing-md)',
            background: '#eff6ff',
            borderRadius: '8px',
            border: '1px solid #bfdbfe',
          }}
        >
          <SmartThermostatIcon size={printMode ? 48 : 64} />
          <div>
            <h4
              style={{
                fontSize: printMode ? '10pt' : 'var(--font-size-md)',
                fontWeight: 600,
                margin: 0,
                color: '#1e40af',
              }}
            >
              Smart Load Compensation
            </h4>
            <p
              style={{
                fontSize: printMode ? '8pt' : 'var(--font-size-sm)',
                margin: '2px 0 0 0',
                color: '#1d4ed8',
              }}
            >
              ±0.1°C precision
            </p>
            <p
              style={{
                fontSize: printMode ? '8pt' : 'var(--font-size-sm)',
                margin: '2px 0 0 0',
                color: '#1e3a8a',
                fontWeight: 600,
              }}
            >
              Minimal waste
            </p>
          </div>
        </div>
      </div>

      {/* Hysteresis Graph */}
      <div style={{ marginBottom: printMode ? '6mm' : 'var(--spacing-lg)' }}>
        <h4 style={subtitleStyle}>Temperature Accuracy Over Time</h4>
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: printMode ? '2mm' : 'var(--spacing-sm)',
            border: '1px solid var(--color-border)',
          }}
        >
          <HysteresisGraph printMode={printMode} />
        </div>
        <p
          style={{
            fontSize: printMode ? '8pt' : 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            marginTop: printMode ? '2mm' : 'var(--spacing-sm)',
            fontStyle: 'italic',
          }}
        >
          Mechanical thermostats cause large temperature swings. Smart load compensation
          modulates the boiler flame to maintain precise comfort.
        </p>
      </div>

      {/* Smart Start Comparison */}
      <div style={{ marginBottom: printMode ? '6mm' : 'var(--spacing-lg)' }}>
        <h4 style={subtitleStyle}>Smart Start: Wake Up Warm</h4>
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: printMode ? '2mm' : 'var(--spacing-sm)',
            border: '1px solid var(--color-border)',
          }}
        >
          <SmartStartComparison printMode={printMode} />
        </div>
        <p
          style={{
            fontSize: printMode ? '8pt' : 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            marginTop: printMode ? '2mm' : 'var(--spacing-sm)',
            fontStyle: 'italic',
          }}
        >
          Smart Start learns your home's thermal mass. On cold mornings, it starts earlier
          to reach exactly 21°C by your wake time.
        </p>
      </div>

      {/* Accuracy Note */}
      <div
        style={{
          background: '#fef9c3',
          border: '1px solid #fde047',
          borderRadius: '8px',
          padding: printMode ? '3mm' : 'var(--spacing-md)',
        }}
      >
        <p
          style={{
            fontSize: printMode ? '9pt' : 'var(--font-size-sm)',
            margin: 0,
            color: '#713f12',
            lineHeight: 1.5,
          }}
        >
          <strong>The 3-Degree Tax:</strong> Bimetallic thermostats can vary by up to 3°C,
          leading to a 10-15% increase in fuel consumption. Smart Load Compensation
          eliminates this by talking directly to the boiler's burner
          {boilerSupportsLoadCompensation
            ? ' - your recommended boiler supports this feature.'
            : ' - note: requires load compensating thermostat compatible boiler.'}
        </p>
      </div>
    </div>
  );
};

export default ControlPrecisionSection;
